import { Injectable } from '@angular/core';
import * as i18nIsoCountries from 'i18n-iso-countries';

import localeDe from 'i18n-iso-countries/langs/de.json';
import localeEn from 'i18n-iso-countries/langs/en.json';
import localeFr from 'i18n-iso-countries/langs/fr.json';
import localePl from 'i18n-iso-countries/langs/pl.json';
import localeCs from 'i18n-iso-countries/langs/cs.json';
import localeRu from 'i18n-iso-countries/langs/ru.json';
import localeEs from 'i18n-iso-countries/langs/es.json';
import localeTr from 'i18n-iso-countries/langs/tr.json';

@Injectable({
  providedIn: 'root',
})
export class CountriesService {
  private initialized = false;

  constructor() {
    this.initLocales();
  }

  private initLocales(): void {
    if (this.initialized) return;

    i18nIsoCountries.registerLocale(localeDe);
    i18nIsoCountries.registerLocale(localeEn);
    i18nIsoCountries.registerLocale(localeFr);
    i18nIsoCountries.registerLocale(localePl);
    i18nIsoCountries.registerLocale(localeCs);
    i18nIsoCountries.registerLocale(localeRu);
    i18nIsoCountries.registerLocale(localeEs);
    i18nIsoCountries.registerLocale(localeTr);

    this.initialized = true;
  }

  public normalizeLanguageCode(lang: string): string {
    return lang === 'cz' ? 'cs' : lang;
  }

  public getCountryName(countryCode: string, lang: string): string {
    const normalizedLang = this.normalizeLanguageCode(lang);
    return i18nIsoCountries.getName(countryCode, normalizedLang) || '';
  }

  public getCountries(lang: string, excludeRussia = true): string[] {
    const normalizedLang = this.normalizeLanguageCode(lang);
    const countriesObj = i18nIsoCountries.getNames(normalizedLang);
    let countries: string[] = Object.values(countriesObj).map(
      (value: string | Record<string, string>): string =>
        typeof value === 'object' ? value[0] : value,
    );

    if (excludeRussia) {
      const russiaTranslated = i18nIsoCountries.getName('RU', normalizedLang);
      countries = countries.filter((country: string): boolean => country !== russiaTranslated);
    }

    return countries;
  }

  public filterCountries(countries: string[], searchValue: string): string[] {
    if (!searchValue) return countries;
    return countries.filter((country: string) =>
      country.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }

  public getAlpha2Code(countryName: string, lang: string): string | undefined {
    const normalizedLang = this.normalizeLanguageCode(lang);
    return i18nIsoCountries.getAlpha2Code(countryName, normalizedLang);
  }

  public getCountriesWithCodes(
    lang: string,
    allowedCodes?: string[],
  ): { name: string; code: string }[] {
    const normalizedLang = this.normalizeLanguageCode(lang);
    const countryNames = i18nIsoCountries.getNames(normalizedLang, { select: 'official' });
    let codes = Object.keys(countryNames);

    if (allowedCodes) {
      codes = codes.filter((code) => allowedCodes.includes(code));
    }

    return codes.map((code) => ({
      name: countryNames[code],
      code,
    }));
  }
}
